// In-app browser notifications (Notification API — NOT web push / service
// worker). These fire only while a staff view is open in the tab; they are the
// in-app replacement for the retired daily-digest email (PRD #260 / ADR-0013).
//
// Two concerns live here:
//
//   1. Device-local enable state + permission handling (`useWebNotifications`).
//      Whether web notifications are enabled is a per-device choice persisted in
//      localStorage — there is no DB column for it (the browser permission is
//      itself device-scoped, so a server flag would be meaningless). The hook
//      reflects both the OS/browser permission and our own enable flag, and
//      exposes `enable()` / `disable()` / `notify()`.
//
//   2. The shared prefs filter (`passesNotificationPrefs`) — the SAME severity
//      floor + per-agent-family mute the email digest applied
//      (`applyFindingFilters` in the server's notification-dispatcher), so the
//      existing Settings toggles govern which alerts surface as web
//      notifications. Pure, so the activity-source hook can unit-test it.

import { useCallback, useEffect, useState } from 'react';
import type { NotificationPrefsDto, SeverityFilter } from '@septima/contract';

const ENABLED_KEY = 'septima.webNotifications.enabled';

// ---------------------------------------------------------------------------
// Browser-API guard
// ---------------------------------------------------------------------------

/** True when this browser exposes the Notification API at all. */
export function webNotificationsSupported(): boolean {
    return typeof window !== 'undefined' && 'Notification' in window;
}

function currentPermission(): NotificationPermission {
    if (!webNotificationsSupported()) return 'denied';
    return Notification.permission;
}

function readEnabledFlag(): boolean {
    try {
        return window.localStorage.getItem(ENABLED_KEY) === 'true';
    } catch {
        return false;
    }
}

function persistEnabledFlag(enabled: boolean): void {
    try {
        if (enabled) window.localStorage.setItem(ENABLED_KEY, 'true');
        else window.localStorage.removeItem(ENABLED_KEY);
    } catch {
        // Private mode / quota — enable state degrades to per-session, fine.
    }
}

// ---------------------------------------------------------------------------
// Prefs filter — mirrors the email digest's applyFindingFilters
// ---------------------------------------------------------------------------

/** A candidate alert distilled to the two axes the prefs filter cares about. */
export interface NotificationCandidate {
    /** Agent family code (AEGIS, PULSE, WARDEN, VIGIL, …) for the per-family mute. */
    agentName: string;
    /** Normalised severity tier — same three-tier scale as the digest. */
    severity: SeverityFilter;
}

// Lower index = more severe. The `severityFilter` pref is a *floor*: a candidate
// passes when its severity is at or above the configured tier. Matches
// SEVERITY_ORDER in the server's notification-dispatcher.
const SEVERITY_ORDER: Record<SeverityFilter, number> = {
    critical: 0,
    attention: 1,
    info: 2,
};

/**
 * Apply the user's `severityFilter` (severity floor) and `agentToggles`
 * (per-family mute: absent key = on, explicit false = muted) to a candidate.
 * Pure — the same gate the email digest used, so the existing Settings toggles
 * govern which alerts become web notifications.
 */
export function passesNotificationPrefs(
    candidate: NotificationCandidate,
    prefs: NotificationPrefsDto | undefined,
): boolean {
    if (!prefs) return true; // No prefs loaded yet — don't suppress.
    const floor = SEVERITY_ORDER[prefs.severityFilter];
    if (SEVERITY_ORDER[candidate.severity] > floor) return false;
    if (prefs.agentToggles[candidate.agentName] === false) return false;
    return true;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface WebNotifications {
    /** True when the browser exposes the Notification API. */
    supported: boolean;
    /** OS/browser permission state ('default' | 'granted' | 'denied'). */
    permission: NotificationPermission;
    /** Device-local enable flag AND permission granted — the gate `notify` checks. */
    enabled: boolean;
    /** Request permission and, on grant, persist enabled=true. Returns the resulting permission. */
    enable: () => Promise<NotificationPermission>;
    /** Clear the device-local enable flag (the browser permission itself is not revocable from JS). */
    disable: () => void;
    /** Fire a browser notification — a no-op unless enabled && permission==='granted'. */
    notify: (opts: { title: string; body?: string; tag?: string }) => void;
}

export function useWebNotifications(): WebNotifications {
    const supported = webNotificationsSupported();
    const [permission, setPermission] = useState<NotificationPermission>(currentPermission);
    const [enabledFlag, setEnabledFlag] = useState<boolean>(readEnabledFlag);

    // If the browser permission was revoked out-of-band (e.g. in site settings),
    // re-sync on mount so a stale enabled flag can't mislead the UI.
    useEffect(() => {
        setPermission(currentPermission());
    }, []);

    const enable = useCallback(async (): Promise<NotificationPermission> => {
        if (!supported) return 'denied';
        const result = await Notification.requestPermission();
        setPermission(result);
        const granted = result === 'granted';
        persistEnabledFlag(granted);
        setEnabledFlag(granted);
        return result;
    }, [supported]);

    const disable = useCallback((): void => {
        persistEnabledFlag(false);
        setEnabledFlag(false);
    }, []);

    const enabled = supported && enabledFlag && permission === 'granted';

    const notify = useCallback(
        (opts: { title: string; body?: string; tag?: string }): void => {
            if (!enabled) return;
            try {
                new Notification(opts.title, { body: opts.body, tag: opts.tag });
            } catch {
                // Some browsers throw if called outside a user gesture or when
                // the page is hidden under certain policies — fail silently.
            }
        },
        [enabled],
    );

    return { supported, permission, enabled, enable, disable, notify };
}
